import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { INDUSTRIES, IMAGES } from "@/data/content";
import { INDUSTRY_DETAILS } from "@/data/industries";
import { PageHero } from "@/components/shared/PageHero";
import { Reveal } from "@/components/shared/Reveal";
import { Icon } from "@/components/shared/Icon";
import { SolutionMiniCard } from "@/components/shared/SolutionMiniCard";
import { ClosingCTA } from "@/components/home/ClosingCTA";
import { getLenis } from "@/components/layout/SmoothScroll";

export default function Industries() {
  useSEO({ title: "Industries | Transportation Technology for Logistics, Schools, Healthcare & Enterprise | Wexoz", description: "Wexoz delivers transportation technology for logistics, delivery, schools, healthcare, retail, corporate transport, passenger transport, government and enterprise fleets across the UAE." });
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) getLenis()?.scrollTo(el, { offset: -100 }) ?? el.scrollIntoView();
    }, 400);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <>
      <PageHero eyebrow="Industries" lines={["Built for organizations that", "move people, goods and vehicles."]} image={IMAGES.dubai}
        text="From individual fleet operators to large enterprise transportation operations, Wexoz designs the technology layer around how each industry actually works." />

      <section className="section">
        <div className="wrap">
          <div className="mb-16 flex flex-wrap gap-2">
            {INDUSTRIES.map((i) => (
              <a key={i.slug} href={`#${i.slug}`} data-testid={`industry-jump-${i.slug}`} className="rounded-full border border-line px-4 py-2 text-xs font-medium text-slate-300 transition-colors hover:border-cyan-400/60 hover:text-white">{i.name}</a>
            ))}
          </div>
          <div className="space-y-6">
            {INDUSTRIES.map((ind, idx) => {
              const d = INDUSTRY_DETAILS[ind.slug];
              return (
                <Reveal key={ind.slug} id={ind.slug} data-testid={`industry-section-${ind.slug}`} className="card grid gap-10 p-8 lg:grid-cols-12 lg:p-10">
                  <div className="lg:col-span-5">
                    <div className="flex items-center justify-between">
                      <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-line bg-navy text-cyan-400"><Icon name={ind.icon} className="h-6 w-6" /></span>
                      <span className="font-mono text-xs text-slate-500">0{idx + 1} / 07</span>
                    </div>
                    <h2 className="mt-8 text-3xl font-semibold leading-tight">{ind.name}</h2>
                    <p className="mt-3 font-heading text-lg text-cyan-400">{ind.text}</p>
                    <p className="mt-5 text-sm leading-relaxed text-slate-400">{d.intro}</p>
                    <Link to={`/contact?industry=${encodeURIComponent(d.formValue)}`} data-testid={`industry-enquire-${ind.slug}`} className="btn-primary mt-8 !py-2.5">Talk to an Expert <ArrowUpRight className="h-4 w-4" /></Link>
                  </div>
                  <div className="lg:col-span-7 grid gap-8 md:grid-cols-2">
                    <div>
                      <div className="eyebrow !text-slate-500">Typical challenges</div>
                      <ul className="mt-4 space-y-3">
                        {d.challenges.map((c) => <li key={c} className="flex items-start gap-2 text-sm text-slate-300"><span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-brand-pink" />{c}</li>)}
                      </ul>
                    </div>
                    <div>
                      <div className="eyebrow !text-slate-500">Relevant solutions</div>
                      <div className="mt-4 space-y-3">
                        {d.solutions.map((s) => <SolutionMiniCard key={s} slug={s} />)}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <ClosingCTA />
    </>
  );
}
